import React, { useEffect, useRef, useState } from 'react';

import { usePaymentApiV1 } from '../../../../clients/quipli-service/payment/payment-api-v1';
import { Typography } from '../../../../components/typography/typography';
import { Modal } from '../../../../components/modal/modal';
import { SelectForm } from '../../../../components/select-form/select-form';
import { Payment, PaymentMethodType, StripeCard } from '../../../../clients/quipli-service/payment/payment';
import useLocalStorage from 'use-local-storage';
import { TerminalFacilitator } from '../terminal-facilitator';
import { Button } from '../../../../components/form/button';
import { CreateRefundRequest, PaymentMethodRequestV2 } from '../../../../clients/quipli-service/payment/payment-v2';
import { usePaymentApiV2 } from '../../../../clients/quipli-service/payment/payment-api-v2';
import { User } from '../../../../clients/quipli-service/user/user';
import { toast } from '../../../../util/toasts';

export const TerminalSelect = ({
  locationId,
  close,
  setCardId,
  setCard,
  poNumber,
  onComplete,
  onCancel,
  setPaymentMethod,
  payments,
  setPayments,
  method,
  setMethod,
  showFacilitator,
  showSetupButton,
  setupIntent,
  user,
  refundRequest,
  invoiceId,
}: {
  locationId: number;
  close: () => void;
  setCardId: (cardId: number) => void;
  setCard?: (stripeCard: StripeCard) => void;
  poNumber?: string;
  onComplete?: (payments?: Payment[]) => void;
  onCancel?: () => void;
  setPaymentMethod?: (pm: PaymentMethodRequestV2) => void;
  payments?: Payment[];
  setPayments?: (payments: Payment[]) => void;
  method?: 'pay' | 'setup' | 'refund';
  setMethod: (method?: 'pay' | 'setup') => void;
  showFacilitator: boolean;
  showSetupButton?: boolean;
  setupIntent?: string;
  user: User;
  refundRequest?: CreateRefundRequest;
  invoiceId?: string;
}) => {
  const { getStripeSettingsAdmin, addTerminalCard } = usePaymentApiV1();
  const { cancelTerminalAction, initiateTerminalRefund } = usePaymentApiV2();
  const [selectedTerminal, setSelectedTerminal] = useLocalStorage<string>(`stripeTerminal-${locationId}`, '');
  const [stripeAccountType, setStripeAccountType] = useState<string>();
  const [internalFacilitate, setInternalFacilitate] = useState<boolean>(false);
  const [setup, setSetup] = useState<string | undefined>(setupIntent);

  const initializedModal = useRef(false);

  async function retrieveStripeAccountType() {
    const accountSettings = await getStripeSettingsAdmin();
    setStripeAccountType(accountSettings.accountType);
  }

  useEffect(() => {
    retrieveStripeAccountType();
  }, []);

  useEffect(() => {
    if (setupIntent) setSetup(setupIntent);
  }, [setupIntent]);

  useEffect(() => {
    setPaymentMethod && setPaymentMethod({ type: PaymentMethodType.Stripe, terminalId: selectedTerminal });
  }, [selectedTerminal]);

  useEffect(() => {
    if (refundRequest && showFacilitator && invoiceId) {
      initiateTerminalRefund(selectedTerminal, invoiceId, refundRequest)
        .catch((e) => {
          console.error('Error initiating refund', e);
          toast('Error initiating refund', 'error');
          close();
        })
        .then(() => console.log('initiated'));
    }
  }, [showFacilitator, refundRequest]);
  const SelectTerminal = ({
    locationId,
    selectedTerminal,
    setSelectedTerminal,
    disabled = false,
  }: {
    locationId: number;
    selectedTerminal?: string;
    setSelectedTerminal: (terminal?: string) => void;
    disabled?: boolean;
  }) => {
    const { getTerminalsForLocations, getTerminalLocations, listTerminals } = usePaymentApiV1();
    const [terminalOptions, setTerminalOptions] = useState<{ label: string; value: string }[]>([]);

    async function populateTerminals() {
      if (stripeAccountType != 'custom') {
        const standardTerminals = await listTerminals(locationId);
        setTerminalOptions(
          standardTerminals.map((t) => {
            return { label: t.label, value: t.id };
          }),
        );
        if (standardTerminals.length == 1) {
          setSelectedTerminal(standardTerminals[0].id);
        }
      } else {
        const locations = await getTerminalLocations();
        const terminals = await getTerminalsForLocations(
          locations
            .filter((l) => {
              return l.locationId == locationId;
            })
            .map((l) => {
              return l.id;
            }),
        );
        setTerminalOptions(
          terminals.map((t) => {
            return { label: t.label, value: t.stripeTerminalId };
          }),
        );
        if (terminals.length == 1) {
          setSelectedTerminal(terminals[0].stripeTerminalId);
        }
      }
    }

    useEffect(() => {
      populateTerminals();
    }, []);

    return (
      <div>
        <Typography type={'text-md'}>Terminals</Typography>
        <div className="flex gap-2 w-full">
          <SelectForm
            className="w-full break-all"
            options={terminalOptions}
            value={terminalOptions.find((t) => {
              return t.value == selectedTerminal;
            })}
            onChange={(t) => setSelectedTerminal(t?.value)}
            menuPlacement="bottom"
            placeholder="Select"
            isMulti={false}
            isDisabled={disabled}
          />
        </div>
      </div>
    );
  };

  return (
    <div id="payTerminal">
      <br />
      <SelectTerminal
        locationId={locationId}
        setSelectedTerminal={setSelectedTerminal}
        selectedTerminal={selectedTerminal}
      />

      <div className="capture-selection-field">
        <br />
        <div className="flex gap-x-6">
          <div className="flex gap-x-1 items-center">
            {method == 'setup' && showSetupButton && (
              <Button
                className={'button button-primary'}
                onClick={async () => {
                  if (!initializedModal.current) {
                    const { setupIntentId: _si } = await addTerminalCard(user.id, locationId, selectedTerminal);
                    setSetup(_si);
                    setMethod('setup');
                    setInternalFacilitate(true);
                    initializedModal.current = true;
                  }
                }}
              >
                <Typography type={'text-sm'}>Present Card</Typography>
              </Button>
            )}
          </div>
        </div>
      </div>

      {selectedTerminal.length > 0 &&
        (showFacilitator || internalFacilitate) &&
        (method == 'pay' || setup || refundRequest) && (
          <Modal
            title={'Swipe or insert card now'}
            isOpen={!!method}
            close={() => {
              cancelTerminalAction(selectedTerminal);
              close();
            }}
            zIndex={10000}
          >
            <TerminalFacilitator
              locationId={locationId}
              paymentMethod={{ terminalId: selectedTerminal, type: PaymentMethodType.Stripe, poNumber }}
              action={method}
              setCardId={setCardId}
              setCard={setCard}
              onCancel={() => {
                setMethod(undefined);
                initializedModal.current = false;
                onCancel && onCancel();
              }}
              onComplete={(payments?: Payment[]) => {
                setInternalFacilitate(false);
                setMethod(undefined);
                initializedModal.current = false;
                onComplete && onComplete(payments);
              }}
              setPayments={setPayments}
              payment={payments?.find((p) => p)}
              close={!method}
              setupIntent={setup}
              user={user}
            />
          </Modal>
        )}
    </div>
  );
};
